import React, { useState } from 'react';
import { api } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

export const EmailImportPanel: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      await api.post('/email-import/config', { email, password });
      setMessage(t("emailSaved"));
      setPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    setMessage('');
    setError('');
    setSyncing(true);
    try {
      await api.post('/email-import/sync');
      setMessage(t("imapCompleted"));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Mailbox sync processing failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm shadow-slate-100 h-full flex flex-col justify-between font-mono text-xs">
      <div>
        <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] mb-4">{t("emailImportTitle")}</h3>
        {error && (
          <div className="bg-rose-50 border border-rose-100 text-rose-600 rounded-xl p-3 text-xs mb-4">
            {error}
          </div>
        )}
        {message && (
          <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-xl p-3 text-xs mb-4">
            {message}
          </div>
        )}
        <form onSubmit={handleSaveConfig} className="space-y-3">
          <div>
            <label htmlFor="imap-email" className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{t("imapUsername")}</label>
            <input
              id="imap-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-indigo-500"
              placeholder="invest@anogilimited.com"
              required
            />
          </div>
          <div>
            <label htmlFor="imap-password" className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{t("imapPassword")}</label>
            <input
              id="imap-password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-indigo-500"
              placeholder="••••••••••••"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white hover:bg-slate-50 disabled:bg-slate-100 text-slate-700 font-semibold py-2 rounded-xl text-sm transition-colors border border-slate-200 shadow-sm"
          >
            {loading ? t("saving") : t("updateCredentials")}
          </button>
        </form>
      </div>

      <div className="mt-6 border-t border-slate-100 pt-4">
        <button
          onClick={handleSyncNow}
          disabled={syncing}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold py-2.5 rounded-xl text-sm transition-colors shadow-sm"
        >
          {syncing ? t("syncingMailbox") : t("syncNow")}
        </button>
      </div>
    </div>
  );
};
